// packages/api/src/hooks/admin/pricing-admin/useDeductibleLevelsConfig.ts
//
// ESCR-200.13 (2026-05-14) — Hook para os 3 templates editáveis de franquia
// (reduzida / basica / ampliada). Consume `deductible_levels_config` table
// (schema ESCR-187.2). Header cards de FranchiseSection mostra os labels
// destes templates; FranchiseLevelsConfigModal edita.
//
// IMPORTANTE: backend admin endpoint
// (`GET/PATCH /admin/pricing/deductible-levels-config`) ainda NÃO existe.
// Schema (`deductible_levels_config`) é consumido apenas pelo
// DeductibleResolverService internamente. Endpoint admin será criado em
// follow-up (ESCR-200.18 — escalado por Pedro 2026-05-14).
//
// Estratégia híbrida (fallback gracioso):
//   - Query tenta GET; se 404 retorna defaults hardcoded da seed
//     (Reduzida -40 / Básica 0 / Ampliada +50) — visualmente idêntico ao
//     hardcoded prévio do FranchiseSection.
//   - Mutation tenta PATCH; se 404 propaga erro 'LEVELS_CONFIG_ENDPOINT_MISSING'
//     pra UI exibir toast informando que feature aguarda backend (ESCR-200.18).
//
// Quando ESCR-200.18 entregar: zero mudança no FE (mesmo hook continua
// funcionando — só `staleness` da cache é re-sincronizada).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  DeductibleLevel,
  DeductibleLevelConfig,
  UpdateDeductibleLevelConfigInput,
} from '@alamo/types';

import { apiClient } from '../../../client';
import { pricingAdminKeys } from '../../../query-keys';

const BASE = '/admin/pricing/deductible-levels-config';

/** Defaults hardcoded (idênticos à seed `deductible_levels_config`). Usado
 *  como fallback quando endpoint admin ainda não existe. */
const FALLBACK_DEFAULTS: ReadonlyArray<DeductibleLevelConfig> = [
  {
    id: 'fallback-reduzida',
    level: 'reduzida',
    defaultPercent: '-40.00',
    description: null,
    updatedBy: null,
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
  },
  {
    id: 'fallback-basica',
    level: 'basica',
    defaultPercent: '0.00',
    description: null,
    updatedBy: null,
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
  },
  {
    id: 'fallback-ampliada',
    level: 'ampliada',
    defaultPercent: '50.00',
    description: null,
    updatedBy: null,
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
  },
];

/** Sentinel error code propagado quando endpoint admin ainda não existe. */
export const LEVELS_CONFIG_ENDPOINT_MISSING = 'LEVELS_CONFIG_ENDPOINT_MISSING';

function is404(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const r = err as { response?: { status?: number } };
  return r.response?.status === 404;
}

export function useDeductibleLevelsConfig() {
  return useQuery({
    queryKey: pricingAdminKeys.deductibleLevelsConfig(),
    queryFn: async (): Promise<ReadonlyArray<DeductibleLevelConfig>> => {
      try {
        const res = await apiClient.get<{ data: DeductibleLevelConfig[] }>(BASE);
        return res.data.data;
      } catch (err) {
        if (is404(err)) {
          // Endpoint não existe — usa defaults hardcoded (ESCR-200.18 follow-up).
          return FALLBACK_DEFAULTS;
        }
        throw err;
      }
    },
    // Defaults são estáveis enquanto endpoint não existe; manter cache
    // longa pra evitar re-fetch desnecessário.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateDeductibleLevelConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      level: DeductibleLevel;
      input: UpdateDeductibleLevelConfigInput;
    }): Promise<DeductibleLevelConfig> => {
      try {
        const res = await apiClient.patch<{ data: DeductibleLevelConfig }>(
          `${BASE}/${args.level}`,
          args.input,
        );
        return res.data.data;
      } catch (err) {
        if (is404(err)) {
          // ESCR-200.18 follow-up — informar UI que endpoint admin ainda não existe.
          throw new Error(LEVELS_CONFIG_ENDPOINT_MISSING);
        }
        throw err;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdminKeys.deductibleLevelsConfig() });
    },
  });
}
